import { useState } from 'react';
import { Card, CardHeader, CardBody, Text, Flex, Box, ButtonIcon } from '@backstage/ui';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import {
  RiRefreshLine,
  RiTeamLine,
  RiGroupLine,
  RiMailLine,
  RiParentLine,
  RiOrganizationChart,
} from '@remixicon/react';
import { MetadataItem } from '../about/metadata-item.component';
import styles from './profiles.module.css';

export const GroupProfileCard = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const { t } = useTranslationRef(appTranslationRef);
  const [refreshing, setRefreshing] = useState(false);

  const spec = entity.spec as Record<string, unknown> | undefined;
  const profile = spec?.profile as Record<string, string> | undefined;
  const type = (spec?.type as string) || undefined;
  const parentName = (spec?.parent as string) || undefined;
  const children = (spec?.children as string[]) || [];

  const displayName = profile?.displayName || entity.metadata.title || entity.metadata.name;
  const email = profile?.email || undefined;
  const description = entity.metadata.description;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const entityRef = `${entity.kind}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
      await catalogApi.refreshEntity(entityRef);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh entity:', error);
    } finally {
      setTimeout(() => setRefreshing(false), 1000);
    }
  };

  return (
    <Card>
      <CardHeader>
        <Flex justify="between" align="center">
          <Flex gap="3" align="center">
            <RiTeamLine size={18} className={styles.headerIcon} />
            <Text variant="title-small" weight="bold">
              {displayName}
            </Text>
          </Flex>
          <ButtonIcon
            icon={<RiRefreshLine size={18} />}
            onPress={handleRefresh}
            isDisabled={refreshing}
            variant="tertiary"
            size="small"
            aria-label={t('groupProfile.refresh')}
          />
        </Flex>
      </CardHeader>
      <CardBody>
        <Flex direction="column" gap="5">
          {description && (
            <Box>
              <Text variant="body-medium" className={styles.descriptionText}>
                {description}
              </Text>
            </Box>
          )}

          <Box className={styles.metadataGrid}>
            {type && (
              <MetadataItem
                icon={<RiGroupLine size={16} />}
                label={t('groupProfile.type')}
                value={type}
              />
            )}
            {email && (
              <MetadataItem
                icon={<RiMailLine size={16} />}
                label={t('groupProfile.email')}
                value={email}
                href={`mailto:${email}`}
              />
            )}
            {parentName && (
              <MetadataItem
                icon={<RiParentLine size={16} />}
                label={t('groupProfile.parent')}
                value={parentName}
                href={`/catalog/default/group/${parentName}`}
              />
            )}
            {children.length > 0 && (
              <MetadataItem
                icon={<RiOrganizationChart size={16} />}
                label={t('groupProfile.children')}
                value={`${children.length}`}
              />
            )}
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};
